import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RequestServiceResponse } from '@tcc-online/request-service';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminJobMatchService, {
    CreateFromProjectTeamPayload,
    CreateFromTeamPayload,
} from '../services/AdminJobMatchService';
import ProjectJobMatching from '../types/jobMatch/ProjectJobMatching';
import ProjectAvailableJobMatching from '../types/jobMatch/ProjectAvailableJobMatching';
import AdminJobMatchingResponse from '../types/jobMatch/AdminJobMatchingResponse';
import UploadState from '../../types/UploadState';
import {
    createNotifiedThunk,
    createAppAsyncThunk,
} from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminJobMatch';

export type JobMatchCreateRejected =
    | { kind: 'empty_team'; status: 409 }
    | { kind: 'validation'; status: 422; message: string };

const getJobMatchings = createNotifiedThunk(
    `${NAME}/getJobMatchings`,
    AdminJobMatchService.getJobMatchings,
    { error: 'fe.admin.error.load-job-matches' },
);
const getAvailableJobMatchings = createNotifiedThunk(
    `${NAME}/getAvailableJobMatchings`,
    AdminJobMatchService.getAvailableJobMatchings,
    { error: 'fe.admin.error.load-job-matches' },
);
const assignProject = createNotifiedThunk(
    `${NAME}/assignProject`,
    AdminJobMatchService.assignProject,
    { error: 'fe.admin.error.assign-project' },
);

const createFromTeam = createAppAsyncThunk<
    RequestServiceResponse<AdminJobMatchingResponse>,
    CreateFromTeamPayload,
    { rejectValue: JobMatchCreateRejected }
>(`${NAME}/createFromTeam`, async (arg, { rejectWithValue }) => {
    try {
        return await AdminJobMatchService.createFromTeam(arg);
    } catch (e: unknown) {
        const status = getResponseStatus(e);
        if (status === 409) {
            return rejectWithValue({ kind: 'empty_team', status: 409 });
        }
        if (status === 422) {
            return rejectWithValue({
                kind: 'validation',
                status: 422,
                message: extract422Message(e),
            });
        }
        throw e;
    }
});

const createFromProjectTeam = createAppAsyncThunk<
    RequestServiceResponse<AdminJobMatchingResponse>,
    CreateFromProjectTeamPayload,
    { rejectValue: JobMatchCreateRejected }
>(`${NAME}/createFromProjectTeam`, async (arg, { rejectWithValue }) => {
    try {
        return await AdminJobMatchService.createFromProjectTeam(arg);
    } catch (e: unknown) {
        const status = getResponseStatus(e);
        if (status === 409) {
            return rejectWithValue({ kind: 'empty_team', status: 409 });
        }
        if (status === 422) {
            return rejectWithValue({
                kind: 'validation',
                status: 422,
                message: extract422Message(e),
            });
        }
        throw e;
    }
});

export type AdminJobMatchState = {
    jobMatchings: UploadState<ProjectJobMatching[]>;
    availableJobMatchings: IBaseState<ProjectAvailableJobMatching[]>;
};

const initialState: AdminJobMatchState = {
    jobMatchings: {
        data: [],
        loading: false,
        uploading: false,
    },
    availableJobMatchings: {
        data: [],
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getJobMatchings.pending, (state) => {
            state.jobMatchings.loading = true;
        });
        builder.addCase(getJobMatchings.fulfilled, (state, action) => {
            state.jobMatchings.data = action.payload.data;
        });
        builder.addCase(getJobMatchings.rejected, (state) => {
            state.jobMatchings.data = [];
        });
        builder.addCase(assignProject.pending, (state) => {
            state.jobMatchings.uploading = true;
        });
        builder.addCase(assignProject.fulfilled, (state, action) => {
            state.jobMatchings.data = action.payload.data;
        });

        builder.addCase(getAvailableJobMatchings.pending, (state) => {
            state.availableJobMatchings.loading = true;
        });
        builder.addCase(getAvailableJobMatchings.fulfilled, (state, action) => {
            state.availableJobMatchings.data = action.payload.data;
        });
        builder.addCase(getAvailableJobMatchings.rejected, (state) => {
            state.availableJobMatchings.data = [];
        });

        builder.addMatcher(
            isAnyOf(getJobMatchings.fulfilled, getJobMatchings.rejected),
            (state) => {
                state.jobMatchings.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(assignProject.fulfilled, assignProject.rejected),
            (state) => {
                state.jobMatchings.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getAvailableJobMatchings.fulfilled,
                getAvailableJobMatchings.rejected,
            ),
            (state) => {
                state.availableJobMatchings.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(createFromTeam.pending, createFromProjectTeam.pending),
            (state) => {
                state.jobMatchings.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                createFromTeam.fulfilled,
                createFromTeam.rejected,
                createFromProjectTeam.fulfilled,
                createFromProjectTeam.rejected,
            ),
            (state) => {
                state.jobMatchings.uploading = false;
            },
        );
    },
});

function getResponseStatus(error: unknown): number | undefined {
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response
            ?.status;
        if (typeof status === 'number') {
            return status;
        }
    }
    return undefined;
}

function extract422Message(error: unknown): string {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (data && typeof data === 'object' && 'errors' in data) {
        const errors = (data as { errors: Record<string, string[] | string> })
            .errors;
        if (errors && typeof errors === 'object') {
            const firstKey = Object.keys(errors)[0];
            if (firstKey) {
                const val = errors[firstKey];
                if (Array.isArray(val) && typeof val[0] === 'string') {
                    return val[0];
                }
                if (typeof val === 'string') {
                    return val;
                }
            }
        }
    }
    if (
        data &&
        typeof data === 'object' &&
        'message' in data &&
        typeof (data as { message: unknown }).message === 'string'
    ) {
        return (data as { message: string }).message;
    }
    return '';
}

const state = (state: AppState): AdminJobMatchState => state.admin.jobMatch;

const AdminJobMatchSlice = {
    reducer: slice.reducer,
    actions: {
        getJobMatchings,
        getAvailableJobMatchings,
        assignProject,
        createFromTeam,
        createFromProjectTeam,
    },
    state: {
        all: state,
        jobMatchings: createSelector(
            state,
            (state: AdminJobMatchState) => state.jobMatchings,
        ),
        availableJobMatchings: createSelector(
            state,
            (state: AdminJobMatchState) => state.availableJobMatchings,
        ),
    },
};

export default AdminJobMatchSlice;
